/* eslint-disable camelcase */
// One form shape for both kinds of report schedule.
//
// The two families post to different endpoints and will keep doing so — that
// is backend work nobody has asked for. What is unified is the thing the user
// touches: a single list of report types, one layout, and one place where the
// answer to "when does it run?" is given. This module owns the translation
// between that single form object and each family's own payload, so the
// dialog itself contains no endpoint knowledge.

import { REPORT_TYPES, isClientReport, buildSchedulePayload, needsPlatforms } from '../clientReports/clientReportModel';
import { DEFAULT_TIMEZONE } from './TimezoneSelect';
import { LEGACY, CLIENT } from './scheduleRows';

export { LEGACY, CLIENT };

// Frequency cards. The legacy dialog's visual cards were the friendliest
// thing in either form, so they are what the unified dialog keeps.
export const FREQUENCY_OPTIONS = [
    { value: 1, label: 'Daily', icon: 'mdi:calendar-today', description: 'Every day', color: '#ef4444' },
    { value: 7, label: 'Weekly', icon: 'mdi:calendar-week', description: 'Every week', color: '#f59e0b' },
    { value: 30, label: 'Monthly', icon: 'mdi:calendar-month', description: 'Every month', color: '#10b981' },
    { value: 365, label: 'Yearly', icon: 'mdi:calendar', description: 'Once a year', color: '#3b82f6' },
];

// Client advertising reports are monthly by contract: the reporting periods
// the backend supports are whole calendar months, and the schedule carries a
// delivery day of the month. Offering Daily on that family would be a control
// that cannot work, so the card list is narrowed rather than disabled — a
// permanently dead card reads as broken.
export const MONTHLY_INTERVAL = 30;
export const WEEKLY_INTERVAL = 7;

export const frequencyOptionsFor = (kind) =>
    kind === CLIENT ? FREQUENCY_OPTIONS.filter((option) => option.value === MONTHLY_INTERVAL) : FREQUENCY_OPTIONS;

export const DAYS_OF_WEEK = [
    { value: 0, label: 'Mon' },
    { value: 1, label: 'Tue' },
    { value: 2, label: 'Wed' },
    { value: 3, label: 'Thu' },
    { value: 4, label: 'Fri' },
    { value: 5, label: 'Sat' },
    { value: 6, label: 'Sun' },
];

export const kindOf = (reportType) => (isClientReport(reportType) ? CLIENT : LEGACY);

// Whether a schedule of this kind is enabled when it is first created.
//
// A new client advertising report must be paused: saving an enabled one
// permits future monthly emails to a client's own inbox, and nobody asked for
// that by picking a report type. CRM schedules have always defaulted to on
// and that is kept — but the two defaults are read from the kind every time
// the type changes, so one can never leak into the other.
export const defaultEnabled = (kind) => kind !== CLIENT;

// The single form object. Keys that cross the wire for client reports keep
// the names `buildSchedulePayload` already expects, so that builder — and the
// date handling the British-date tests pin — is reused rather than rewritten.
export const emptyScheduleForm = (practiceId) => ({
    id: null,
    report_type: '',
    // Shared: who it goes to, when it runs, whether it is live.
    recipients: [],
    time_of_day: '09:00',
    is_enabled: true,
    interval: null,
    day_of_the_week: null,
    delivery_day: 3,
    timezone: DEFAULT_TIMEZONE,
    // CRM only.
    additional_context: {},
    // Client advertising only.
    title: '',
    practice_ids: practiceId ? [Number(practiceId)] : [],
    platforms: ['google_ads', 'meta'],
    period: 'previous_month',
    date_from: '',
    date_to: '',
    formats: ['PDF'],
});

// A brand-new schedule, optionally with its type already chosen (the
// analytics tables deep-link here with one). The cadence and the live/paused
// default are read from the chosen type's family, never carried over.
export const newScheduleForm = (practiceId, reportType = '') => {
    const base = emptyScheduleForm(practiceId);
    if (!reportType) return base;
    const kind = kindOf(reportType);
    return {
        ...base,
        report_type: reportType,
        is_enabled: defaultEnabled(kind),
        interval: kind === CLIENT ? MONTHLY_INTERVAL : null,
    };
};

// A saved client record arrives in exactly the client payload shape, so it
// merges straight in; only the monthly interval has to be asserted, because
// the client resource has no interval field of its own.
export const clientFormToSchedule = (practiceId, saved = {}) => ({
    ...emptyScheduleForm(practiceId),
    is_enabled: false,
    ...saved,
    interval: MONTHLY_INTERVAL,
    report_type: saved.report_type || 'ADVERTISING_SPEND',
});

// A saved CRM record. `time_of_day` is HH:mm:ss on that endpoint and HH:mm in
// the form, so the seconds are dropped here and re-added on the way out — one
// place, rather than two pickers disagreeing about the format.
export const legacyFormToSchedule = (practiceId, row = {}) => ({
    ...emptyScheduleForm(practiceId),
    id: row.id ?? null,
    report_type: row.report_type || '',
    interval: row.interval ?? null,
    day_of_the_week: typeof row.day_of_the_week === 'number' ? row.day_of_the_week : null,
    time_of_day: String(row.time_of_day || '').slice(0, 5),
    is_enabled: Boolean(row.is_enabled),
    recipients: normaliseRecipients(row.recepients),
    additional_context:
        row.additional_context && typeof row.additional_context === 'object' ? row.additional_context : {},
});

// Older CRM rows stored recipients as a comma string. Normalise on load so
// the chip control always receives an array.
export function normaliseRecipients(value) {
    if (Array.isArray(value)) return value;
    return String(value || '')
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean);
}

export const cleanRecipients = (list) =>
    [...new Set((list || []).flatMap((entry) => String(entry).split(/[;,\s]+/)).map((entry) => entry.trim()))].filter(
        Boolean
    );

export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// The CRM payload, unchanged from the form it replaced: same endpoint, same
// field names, same `recepients` spelling the backend has always used.
export function buildLegacyPayload(form) {
    const interval = Number(form.interval);
    return {
        is_enabled: Boolean(form.is_enabled),
        report_type: form.report_type,
        interval,
        recepients: cleanRecipients(form.recipients),
        time_of_day: form.time_of_day ? `${form.time_of_day}:00` : null,
        // Only sent on a weekly schedule: the backend's cron-day filter
        // ignores it otherwise, and storing it would leave a stale day on the
        // row for whoever reads it next.
        day_of_the_week: interval === WEEKLY_INTERVAL ? form.day_of_the_week : null,
        additional_context: form.additional_context || {},
    };
}

export { buildSchedulePayload };

// Reasons this form cannot be saved yet, in the user's words. Both families
// use the same list, so the dialog has one disabled-save rule and one place
// that explains it, rather than a silent button on one type and an error
// panel on the other.
export function scheduleIssues(form, context = {}) {
    const { locationsLoading = false, locationsError = '', timezoneValid = true } = context;
    const issues = [];
    if (!form.report_type) issues.push('Choose a report type.');
    const kind = kindOf(form.report_type);
    const emails = cleanRecipients(form.recipients);
    if (emails.length === 0) issues.push('Add at least one recipient.');
    else if (emails.length > 20) issues.push('Up to 20 recipients.');
    else if (!emails.every((email) => EMAIL_PATTERN.test(email)))
        issues.push('Check the highlighted email addresses.');
    if (!form.report_type) return issues;
    if (kind === LEGACY) {
        if (!form.interval) issues.push('Choose how often it runs.');
        // The backend accepts a null weekday on a weekly schedule and quietly
        // normalises it to Monday, so a cleared Friday would become Monday
        // with no sign of it here. Weekly must name its day.
        else if (Number(form.interval) === WEEKLY_INTERVAL && !Number.isInteger(form.day_of_the_week))
            issues.push('Choose which day it runs.');
        return issues;
    }
    if (!form.title.trim()) issues.push('Give the report a name.');
    if (locationsLoading) issues.push('Locations are still loading.');
    if (locationsError) issues.push('Locations are unavailable.');
    if (form.practice_ids.length === 0) issues.push('Choose at least one location.');
    if (form.practice_ids.length > 25) issues.push('Up to 25 locations.');
    if (needsPlatforms(form.report_type) && !form.platforms.length) issues.push('Choose at least one platform.');
    if (!form.formats.length) issues.push('Choose at least one download format.');
    if (!timezoneValid) issues.push('Choose a timezone from the list.');
    if (form.period === 'custom')
        issues.push('A custom date range can be previewed and downloaded, but not scheduled.');
    if (!form.time_of_day) issues.push('Choose a delivery time.');
    const day = Number(form.delivery_day);
    if (!(day >= 1 && day <= 31)) issues.push('Choose a delivery day between 1 and 31.');
    return issues;
}

// Whether the scope and date controls behind "More options" already hold a
// value. Opening a schedule whose advanced settings are in use must not hide
// them behind a closed disclosure — the user would not know they were there.
export function hasAdvancedValues(form) {
    if (form.period === 'custom' || form.date_from || form.date_to) return true;
    const scope = form.additional_context || {};
    return Boolean((scope.source_types || []).length || (scope.practice_user_ids || []).length);
}

// The one type list, both families together, so nobody has to know which
// family their report belongs to before choosing it.
export function scheduleTypeGroups(crmReportTypes) {
    const crm = (crmReportTypes || [])
        .filter((option) => option?.value && !isClientReport(option.value))
        .map((option) => ({ value: option.value, label: option.label || option.value, kind: LEGACY }));
    const client = Object.entries(REPORT_TYPES).map(([value, label]) => ({ value, label, kind: CLIENT }));
    return [
        { kind: LEGACY, label: 'CRM reports', hint: 'Emailed from this practice’s own CRM data', options: crm },
        {
            kind: CLIENT,
            label: 'Client reports',
            hint: 'Multi-location advertising document with PDF and Excel downloads',
            options: client,
        },
    ].filter((group) => group.options.length > 0);
}

export const scheduleTypeLabel = (groups, value) => {
    const found = groups.flatMap((group) => group.options).find((option) => option.value === value);
    return found?.label || value || '';
};
